import { ThemeConfig } from '../index';
import { getThemeColors } from '../../themes/defaults';
import {
  DEFAULT_CHART_THEME_COLORS,
  resolveDesignColor,
} from '@json-to-office/shared';

export type ColorName = keyof ReturnType<typeof getThemeColors>;

// Union type for all valid color names
export type ValidColorName =
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'text'
  | 'background'
  | 'textPrimary'
  | 'textSecondary'
  | 'textMuted'
  | 'borderPrimary'
  | 'borderSecondary'
  | 'backgroundPrimary'
  | 'backgroundSecondary'
  | 'rule'
  | 'onPrimary'
  | 'surfaceInverse'
  | 'positive'
  | 'negative';

// Type for color value that can be either a hex string or a color name
export type ColorValue = string; // Can be hex color or color name

/**
 * Resolves a color value, which can be either a hex color string or a color name reference
 * @param colorValue - Either a hex color (e.g., '#000000') or a color name (e.g., 'primary')
 * @param theme - The theme configuration containing color definitions
 * @returns The resolved hex color value (6-character hex without #)
 * @throws Error if the color value is invalid
 */
export function resolveColor(
  colorValue: ColorValue,
  theme: ThemeConfig
): string {
  // Check if it's a hex color with # prefix
  if (colorValue.startsWith('#')) {
    const hexValue = colorValue.slice(1);
    if (/^[0-9A-Fa-f]{6}$/.test(hexValue)) {
      return hexValue.toUpperCase(); // Normalize to uppercase
    }
    // Invalid hex format
    throw new Error(
      `Invalid hex color: "${colorValue}". Hex colors must be in format #RRGGBB (e.g., "#000000").`
    );
  }

  // If it's a color name, resolve it from the theme. Optional slots
  // (accent4-6) may be present-but-undefined, which counts as unset.
  const colors = getThemeColors(theme);
  const resolvedColor = colors[colorValue as ColorName];
  if (typeof resolvedColor === 'string') {
    const hex = resolveDesignColor(colorValue, colors);
    if (hex) return hex.toUpperCase();
    throw new Error(
      `Unresolvable theme color: "${colorValue}" (missing reference or cycle).`
    );
  }

  // Bare 6-digit hex, e.g. "F0FDF4". HexColorSchema admits it through the
  // theme-name branch, so rejecting it here would validate a document and then
  // throw while rendering it. Table cells and the chart palette already accept
  // this form; no theme color name is six hex characters, so there is no
  // ambiguity with a real token.
  if (/^[0-9A-Fa-f]{6}$/.test(colorValue)) {
    return colorValue.toUpperCase();
  }

  // Strict validation - throw error for invalid colors
  throw new Error(
    `Invalid color value: "${colorValue}". Must be a hex color with # prefix (e.g., "#000000") or a valid theme color name.`
  );
}

/**
 * Validates if a color name is set in the theme
 * @param colorName - The color name to validate
 * @param theme - The theme configuration
 * @returns True if the color name resolves to a value. A present-but-undefined
 *   optional slot counts as unset, matching what `resolveColor` accepts.
 */
export function isValidColorName(
  colorName: string,
  theme: ThemeConfig
): boolean {
  const colors = getThemeColors(theme);
  if (typeof colors[colorName as ColorName] !== 'string') return false;
  // Follow the reference chain: a token whose value names an unset slot is not
  // usable, so reporting it as valid would hand a caller a color that throws.
  try {
    resolveColor(colorName as ColorValue, theme);
    return true;
  } catch {
    return false;
  }
}

/**
 * Gets all available color names from a theme
 * @param theme - The theme configuration
 * @returns Array of valid color names, excluding present-but-undefined slots
 */
export function getAvailableColorNames(theme: ThemeConfig): string[] {
  const colors = getThemeColors(theme);
  return Object.keys(colors).filter((name) => isValidColorName(name, theme));
}

/**
 * The values an implicit DOCX chart palette is built from, in palette order.
 *
 * `palette.chart` wins outright when the theme declares it; otherwise the
 * legacy series tokens contribute the value they store, skipping slots the
 * theme leaves unset (see DEFAULT_CHART_THEME_COLORS). Each caller resolves
 * these with its own policy — the native chart compiler throws on an
 * unresolvable value, the Highcharts component drops it — so only the choice
 * of source lives here, and it lives here once.
 */
export function chartPaletteValues(theme: ThemeConfig): string[] {
  const explicit = theme.palette?.chart;
  if (explicit) return [...explicit];
  const colors = getThemeColors(theme) as Record<string, string | undefined>;
  return DEFAULT_CHART_THEME_COLORS.flatMap((token) => {
    const value = colors[token];
    return typeof value === 'string' && value.length > 0 ? [value] : [];
  });
}
